import * as semver from 'semver';
import { isDevBearerMode, getJwtTokenFromCookie } from '@/composables/nestApi';
// eslint-disable-next-line import/no-cycle
import { fetchMigrationV5Status, isServedByV5 } from '@/composables/migrationV5';

const V4_REDIRECT_EXEMPT_PREFIXES = [
  '/auth/',
  '/account/activation',
  '/send-domains/activation',
  '/shopify/',
  '/complete/',
  '/choose-shop',
  // Subscription management is shared between platforms : a V4-platform shop
  // accessing /shop-subscription on the V5 frontend must NOT be redirected
  // back to its V4 dashboard. The backend exposes the right `capabilities`
  // based on the JWT's `shop_version` so the V5 UI adapts itself.
  '/shop-subscription',
  // `/shop/bills` is now a router-level redirect to `/shop-subscription`.
  // Must stay exempt so the redirect is allowed to resolve — otherwise a V4
  // shop hitting this URL (old bookmark, V4 sidebar link) would be bounced
  // to the V4 dashboard before the redirect ever fires.
  '/shop/bills',
  // `/migration-v5/` est servi côté V5 pour les shops EN COURS de migration
  // V4 → V5. Ces shops ont encore `spm_platform_version=v4` (resolveShopVersion
  // ne lit pas `platform_migration_v5_pending`, seulement `migration_pending`).
  // Sans cette exemption, V4 redirige vers V5 → V5 bounce vers V4 → boucle.
  '/migration-v5',
];

export function isV4RedirectExemptPath(path: string): boolean {
  return V4_REDIRECT_EXEMPT_PREFIXES.some((prefix) => path.startsWith(prefix));
}

function decodeJwtShopVersion(jwt: string): 'v4' | 'v5' | null {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const v = payload?.shop_version;
    return v === 'v4' || v === 'v5' ? v : null;
  } catch {
    return null;
  }
}

export function getShopPlatformVersion(): 'v4' | 'v5' | null {
  const match = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('='))
    .find(([name]) => name === 'spm_platform_version');

  if (match) {
    const value = decodeURIComponent(match[1] ?? '');
    if (value === 'v4' || value === 'v5') return value;
  }

  if (isDevBearerMode()) {
    const token = getJwtTokenFromCookie();
    if (token) return decodeJwtShopVersion(token);
  }

  return null;
}

export function getV4DashboardUrl(): string {
  return process.env.VUE_APP_V4_DASHBOARD_URL || 'https://my.shopimind.com';
}

export async function redirectIfV4Shop(path: string): Promise<boolean> {
  if (isV4RedirectExemptPath(path)) return false;
  if (getShopPlatformVersion() !== 'v4') return false;

  // Le cookie `spm_platform_version` dit `v4`, mais un shop EN COURS de
  // migration OU FINALISÉ (pas encore re-loggé → cookie encore `v4`) est
  // légitimement servi par V5. On confirme via le statut migration (source de
  // vérité backend) avant de bouncer — sinon on renvoie un shop migré /
  // finalisé vers V4, qui le re-redirige sur V5 → boucle infinie my <-> my.new.
  const status = await fetchMigrationV5Status();
  if (isServedByV5(status)) return false;

  window.location.href = getV4DashboardUrl();
  return true;
}

/**
 * Vérifie via les données chargées par /auth/me si le shop courant est en v5.
 * Retourne true si:
 *  - currentVersion vide/non définie (onboarding pas terminé, on ne sait pas encore)
 *  - currentVersion >= 5.0.0
 * Retourne false uniquement quand currentVersion est explicitement < 5.0.0.
 */
export function isShopCurrentVersionV5(currentVersion: string | null | undefined): boolean {
  if (!currentVersion) return true;
  try {
    return semver.gte(currentVersion, '5.0.0');
  } catch {
    return true;
  }
}
